import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, MapPin, Clock, Heart, RefreshCw } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from '@/contexts/LocationContext';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import SEO, { placeSchema } from '@/components/SEO';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { OpenStatusBadge } from '@/components/OpenStatusBadge';
import { MarkdownText } from '@/components/MarkdownText';
import { PlaceLinks } from '@/components/PlaceLinks';
import { ShareButton } from '@/components/ShareButton';
import { KidsBadge, OfficialGuachincheNote } from '@/components/PlaceBadges';

const PlaceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, getLocalizedField, language } = useLanguage();
  const { location } = useLocation();
  const { user } = useAuth();
  
  const [place, setPlace] = useState(null);
  const [placeCategories, setPlaceCategories] = useState([]);
  useEffect(() => { api.getCategories().then((r) => setPlaceCategories(r.data)).catch(() => {}); }, []);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const galleryRef = useRef(null);
  const handleGalleryScroll = () => {
    const el = galleryRef.current;
    if (!el || !el.clientWidth) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== activeImage) setActiveImage(idx);
  };
  const scrollGalleryTo = (i) => {
    galleryRef.current?.scrollTo({ left: i * galleryRef.current.clientWidth, behavior: 'smooth' });
    setActiveImage(i);
  };

  const loadPlace = useCallback(async (refreshGoogle = false) => {
    if (refreshGoogle) {
      setRefreshing(true);
    }
    try {
      const res = await api.getPlace(id, location?.lat, location?.lng, refreshGoogle);
      setPlace(res.data);
      if (refreshGoogle) {
        toast.success(language === 'es' ? 'Datos actualizados de Google' : language === 'de' ? 'Daten von Google aktualisiert' : 'Data updated from Google');
      }
    } catch (error) {
      console.error('Error loading place:', error);
      toast.error('Error al cargar el lugar');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, location, language]);

  useEffect(() => {
    loadPlace(false);
  }, [loadPlace]);

  useEffect(() => {
    if (!user || !place) return;
    api.checkFavorite(place.id)
      .then((res) => setIsFavorite(res.data.is_favorite))
      .catch((error) => console.error('Error checking favorite:', error));
  }, [user, place]);

  const handleFavoriteClick = async () => {
    if (!user) {
      toast.error('Inicia sesión para guardar favoritos');
      return;
    }

    try {
      if (isFavorite) {
        await api.removeFavorite(place.id);
        setIsFavorite(false);
        toast.success(t('removeFromFavorites'));
      } else {
        await api.addFavorite(place.id);
        setIsFavorite(true);
        toast.success(t('addToFavorites'));
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  if (loading) {
    return (
      <div className="pb-20">
        <Skeleton className="h-[50vh] w-full" />
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-6 w-1/4" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!place) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Lugar no encontrado</p>
      </div>
    );
  }

  const name = getLocalizedField(place, 'name');
  const description = getLocalizedField(place, 'description');
  const images = place.images?.length > 0 ? place.images : ['https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800'];

  return (
    <div className="pb-20" data-testid="place-detail-page">
      {place && (
        <SEO
          title={getLocalizedField(place, 'name')}
          description={getLocalizedField(place, 'description')?.replace(/[#*`_]+/g, '').replace(/\s+/g, ' ').trim().slice(0, 160) || `${getLocalizedField(place, 'name')} - Tenerife`}
          path={`/place/${place.id}`}
          image={place.images?.[0]}
          lang={language}
          schema={placeSchema(place)}
        />
      )}
      {/* Image gallery */}
      <div className="relative h-[50vh] min-h-[300px] overflow-hidden">
        <div
          ref={galleryRef}
          onScroll={handleGalleryScroll}
          className="flex h-full w-full overflow-x-auto snap-x snap-mandatory scrollbar-hide"
          style={{ overscrollBehaviorX: 'contain', WebkitOverflowScrolling: 'touch' }}
          data-testid="place-gallery"
        >
          {images.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={images.length > 1 ? `${name} ${i + 1}` : name}
              loading={i === 0 ? 'eager' : 'lazy'}
              draggable={false}
              className="w-full h-full flex-shrink-0 snap-center object-cover select-none"
            />
          ))}
        </div>
        
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute safe-top-offset left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
        </button>

        {/* Share + Favorite buttons */}
        <ShareButton kind="p" itemId={place.id} variant="round" className="absolute safe-top-offset right-16" testId="share-place-btn" />
        <button
          onClick={handleFavoriteClick}
          className={`absolute safe-top-offset right-4 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all ${
            isFavorite
              ? 'bg-[#E29578] text-white'
              : 'bg-white/90 backdrop-blur-sm text-gray-600 hover:text-[#E29578]'
          }`}
          data-testid="favorite-btn"
        >
          <Heart size={20} fill={isFavorite ? 'currentColor' : 'none'} />
        </button>

        {/* Caption of active photo (uploaded via publisher) */}
        {(() => {
          const photo = place.photos?.[activeImage];
          const caption = photo && (photo[`caption_${language}`] || photo.caption_es);
          return caption ? (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent pt-10 pb-9 px-4 pointer-events-none" data-testid="gallery-caption">
              <p className="text-white text-sm leading-snug drop-shadow line-clamp-2">{caption}</p>
            </div>
          ) : null;
        })()}

        {/* Image dots */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollGalleryTo(i)}
                aria-label={`Foto ${i + 1}`}
                data-testid={`gallery-dot-${i}`}
                className={`h-2 rounded-full transition-all ${
                  i === activeImage ? 'bg-white w-6' : 'bg-white/50 w-2'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1D1D1F] mb-2">
            {name}
          </h1>

          {(place.categories?.length > 0 || place.category) && (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="place-category-chips">
              {(place.categories?.length ? place.categories : [place.category]).map((slug, i) => (
                <button key={slug} onClick={() => navigate(`/explore?category=${slug}`)}
                  className={`text-xs font-semibold px-3 py-1 transition-colors ${i === 0 ? 'bg-[#38B2AC] text-white' : 'border border-[#38B2AC] text-[#38B2AC] hover:bg-[#38B2AC]/10'}`}
                  data-testid={`place-category-chip-${slug}`}>
                  {placeCategories.find((c) => c.slug === slug)?.[`name_${language}`] || placeCategories.find((c) => c.slug === slug)?.name_es || slug}
                </button>
              ))}
              {place.kids_friendly && <KidsBadge testId="kids-badge-detail" />}
            </div>
          )}
          {place.official_guachinche && <div className="mb-4"><OfficialGuachincheNote checkedAt={place.official_guachinche_checked_at} /></div>}
          
          <div className="flex flex-wrap items-center gap-4">
            {/* Rating */}
            {place.google_rating && (
              <div className="flex items-center gap-1">
                <Star size={18} className="text-yellow-500" fill="currentColor" />
                <span className="font-semibold">{place.google_rating}</span>
                {place.google_reviews_count && (
                  <span className="text-gray-500">({place.google_reviews_count.toLocaleString()} {t('reviews')})</span>
                )}
                {place.google_place_id && (
                  <button
                    onClick={() => loadPlace(true)}
                    disabled={refreshing}
                    className="ml-2 p-1 text-gray-400 hover:text-[#38B2AC] transition-colors disabled:opacity-50"
                    title={language === 'es' ? 'Actualizar datos de Google' : language === 'de' ? 'Google-Daten aktualisieren' : 'Refresh Google data'}
                    data-testid="refresh-google-btn"
                  >
                    <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                  </button>
                )}
              </div>
            )}

            {/* Distance */}
            {place.distance !== undefined && (
              <div className="flex items-center gap-1 text-[#38B2AC]">
                <MapPin size={16} />
                <span className="font-medium">
                  {place.distance < 1
                    ? `${Math.round(place.distance * 1000)} m`
                    : `${place.distance} ${t('kmAway')}`}
                </span>
              </div>
            )}
            <OpenStatusBadge openingHours={place.opening_hours} size="lg" placeId={place.id} />
          </div>
        </div>

        {/* Links row: Google Maps + socials + web + phone (only filled ones) */}
        <div className="mb-8">
          <PlaceLinks place={place} />
        </div>

        {/* Description */}
        <div className="mb-8">
          <h2 className="font-semibold text-lg mb-3">Descripción</h2>
          <MarkdownText text={description} className="text-gray-600 leading-relaxed" />
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {place.address && (
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <MapPin className="text-[#38B2AC] mt-1" size={20} />
                <div>
                  <p className="font-medium mb-1">Dirección</p>
                  <p className="text-gray-600 text-sm">{place.address}</p>
                </div>
              </div>
            </div>
          )}

          {place.opening_hours && place.opening_hours.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-4 md:col-span-2">
              <div className="flex items-start gap-3">
                <Clock className="text-[#38B2AC] mt-1" size={20} />
                <div>
                  <p className="font-medium mb-1">{t('openingHours')}</p>
                  <div className="mb-2"><OpenStatusBadge openingHours={place.opening_hours} placeId={`${place.id}-hours`} /></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                    {place.opening_hours.map((hours, i) => (
                      <p key={i} className="text-gray-600 text-sm">{hours}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaceDetailPage;
